import { Route, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useAuthStore } from "./store/authStore";

// Direct imports (for critical routes - Login & Public Profile)
import LoginPage from "./pages/auth/LoginPage";
import PublicProfilePage from "./pages/portfolio/preview";

// Components
import { ProtectedRoute } from "./components/ProtectedRoute";
import RouteLoadingFallback from "./components/RouteLoadingFallback";

// Lazy loaded pages
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const ForgotPasswordPage = lazy(
  () => import("./pages/auth/ForgotPasswordPage")
);
const DashboardLayout = lazy(() => import("./layouts/dashboard"));
const DashboardHome = lazy(() => import("./pages/dashboard"));
const Cards = lazy(() => import("./pages/dashboard/cards"));
const Analytics = lazy(() => import("./pages/analytics"));
const Settings = lazy(() => import("./pages/settings"));
const PortfolioManagement = lazy(
  () => import("./pages/portfolio/PortfolioManagement")
);
const ManageCards = lazy(() => import("./pages/cards/ManageCards"));
const SelectCard = lazy(() => import("./pages/cards/SelectCard"));
const CreateCard = lazy(() => import("./pages/cards/CreateCard"));
const ConnectionsPage = lazy(() => import("./pages/connections"));
const ConnectionListing = lazy(
  () => import("./pages/connections/ConnectionListing")
);
const ConnectionRequests = lazy(
  () => import("./pages/connections/ConnectionRequests")
);
const SharePage = lazy(() => import("./pages/share"));
const RemoteRoom = lazy(() => import("./pages/share/RemoteRoom"));
const SearchPage = lazy(() => import("./pages/search"));

function App() {
  const { isAuthenticated } = useAuthStore();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* Auth Routes */}
          <Route
            path="/auth/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage />
              )
            }
          />
          <Route
            path="/auth/register"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <RegisterPage />
              )
            }
          />
          <Route
            path="/auth/forgot-password"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <ForgotPasswordPage />
              )
            }
          />

          {/* Public Profile Page */}
          <Route path="/p" element={<PublicProfilePage />} />
          <Route
            path="/portfolio/preview/:cardId"
            element={<PublicProfilePage />}
          />

          {/* Portfolio Management */}
          <Route
            path="/portfolio/manage/:cardId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <PortfolioManagement />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Cards Management */}
          <Route
            path="/cards/manage/:orgId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ManageCards />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Card Selection */}
          <Route
            path="/cards/select/:orgId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SelectCard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Card Creation */}
          <Route
            path="/cards/create/:orgId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CreateCard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Card Edit */}
          <Route
            path="/cards/edit/:orgId/:cardId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CreateCard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Protected Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardHome />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/cards"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Cards />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/analytics"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Analytics />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Settings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Connections Routes */}
          <Route
            path="/connections"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ConnectionsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/connections/requests"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ConnectionRequests />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/connections/:folderId"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <ConnectionListing />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Share Routes */}
          <Route
            path="/share"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SharePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/share/remote/:code"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <RemoteRoom />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Search Route */}
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SearchPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Redirects */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />

          {/* Catch all - redirect */}
          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
